// ABOUTME: Fills in the street and city of a Dutch address once postal code and house number are entered.
// ABOUTME: Debounces the PDOK lookup behind /general/address-lookup, drops outdated answers and reports loading/not found.
import { useEffect, useRef, useState } from "react";
import { useLazyLookupDutchAddressQuery } from "../newapi/global/generalApiSlice";
import { countryCodeNL } from "../utils/constants";

const DUTCH_POSTAL_CODE = /^\d{4}\s?[A-Z]{2}$/i;
const LOOKUP_DELAY_MS = 400;

const useDutchStreetLookup = ({ country, postalCode, houseNumber }, onFound) => {
    const [lookup] = useLazyLookupDutchAddressQuery();
    // idle | loading | notFound
    const [status, setStatus] = useState("idle");

    // Keep the latest callback without restarting the lookup on every render.
    const onFoundRef = useRef(onFound);
    onFoundRef.current = onFound;

    useEffect(() => {
        const postal = (postalCode || "").trim();
        const number = (houseNumber || "").trim();
        if ((country || "").toLowerCase() !== countryCodeNL || !DUTCH_POSTAL_CODE.test(postal) || !/^\d+$/.test(number)) {
            setStatus("idle");
            return;
        }

        let cancelled = false;
        setStatus("loading");
        const timer = setTimeout(async () => {
            const result = await lookup({ postalCode: postal.replace(/\s+/g, "").toUpperCase(), houseNumber: number }, true);
            if (cancelled) return;
            if (result.data?.success) {
                onFoundRef.current({ street: result.data.street, city: result.data.city });
                setStatus("idle");
            } else {
                setStatus("notFound");
            }
        }, LOOKUP_DELAY_MS);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [country, postalCode, houseNumber, lookup]);

    return { isLoading: status === "loading", notFound: status === "notFound" };
};

export default useDutchStreetLookup;
